import { FC, useMemo } from 'react';
import NumberFlow from '@number-flow/react';
import { Col, Grid, Row } from 'antd';
import { capitalCase } from 'change-case';

import { BillingInterval, PLAN_CONFIG, PlanKey } from '@/constants';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import PlanPopover from '../../popovers/PlanPopover';
import { calculateEducationDiscountPreview, EducationDiscountStatus } from './pricing';

export interface CostPreviewProps {
    educationDiscountStatus: EducationDiscountStatus;
    interval: BillingInterval;
    plan: PlanKey;
    seats: number;
}

const { useBreakpoint } = Grid;

const CostPreview: FC<CostPreviewProps> = ({ educationDiscountStatus, interval, plan, seats }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const discountPreview = useMemo(
        () => calculateEducationDiscountPreview(plan, interval, seats, educationDiscountStatus),
        [educationDiscountStatus, interval, plan, seats],
    );
    const screens = useBreakpoint();

    return (
        <Row gutter={[8, 8]} style={{ marginTop: '0.5rem', width: '100%' }} wrap>
            <Col md={18} order={1} xs={24}>
                <div style={{ marginBottom: '0.5rem' }}>
                    <small style={{ color: token.colorTextTertiary }}>Seat Breakdown by Plan</small>
                </div>
                <span style={{ color: token.colorTextTertiary }}>
                    <strong style={{ color: token.colorText }}>
                        <NumberFlow value={seats} />x Seats
                    </strong>{' '}
                    for the{' '}
                    <strong style={{ color: token.colorText }}>
                        <PlanPopover plan={PLAN_CONFIG[plan].key as PlanKey} />
                    </strong>{' '}
                    on a <strong style={{ color: token.colorText }}>{capitalCase(interval)}</strong> Billing Cycle
                </span>
            </Col>
            <Col md={6} order={2} style={{ textAlign: screens.md ? 'right' : 'left' }} xs={24}>
                <div style={{ marginBottom: '0.5rem' }}>
                    <small style={{ color: token.colorTextTertiary }}>Cost Preview</small>
                </div>
                {discountPreview.discountedTotal === null ? (
                    'Custom'
                ) : (
                    <strong style={{ color: token.colorText }}>
                        {discountPreview.hasDiscount && discountPreview.originalTotal !== null ? (
                            <>
                                <span
                                    style={{
                                        color: token.colorTextTertiary,
                                        marginRight: '0.5rem',
                                        textDecoration: `line-through ${token.colorTextTertiary} solid 1px`,
                                    }}
                                >
                                    ${discountPreview.originalTotal}
                                </span>
                                <span style={{ color: token.colorSuccess }}>
                                    $<NumberFlow value={discountPreview.discountedTotal} />
                                </span>
                            </>
                        ) : (
                            <>
                                $<NumberFlow value={discountPreview.discountedTotal} />
                            </>
                        )}
                    </strong>
                )}
            </Col>
        </Row>
    );
};

export default CostPreview;
